/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Select, Button, Space, Typography } from 'antd'
import {
	SearchOutlined,
	ReloadOutlined,
	ClearOutlined,
} from '@ant-design/icons'
import {
	PricingFilters as PricingFiltersType,
	VendorType,
	CurrencyType,
	ChannelType,
} from 'types'
import pricingService from 'api/pricing/pricingService'

const { Option } = Select

// eslint-disable-next-line no-unused-vars
interface PricingFiltersProps {
	onFiltersChange: (filters: PricingFiltersType) => void
	onRefresh: () => void
}

/**
 * Pricing Filters Component
 * Provides filtering options for vendor, currency, country, and channel
 */
const PricingFilters: React.FC<PricingFiltersProps> = ({
	onFiltersChange,
	onRefresh,
}) => {
	const [localFilters, setLocalFilters] = useState<PricingFiltersType>({})
	const [countries, setCountries] = useState<
		Array<{ code: string; name: string; phone_code: string }>
	>([])
	const [loadingCountries, setLoadingCountries] = useState(false)

	/**
	 * Vendor options
	 */
	const vendorOptions: Array<{ value: VendorType; label: string }> = [
		{ value: 'GUPSHUP', label: 'Gupshup' },
		{ value: 'GUPSHUP_ENTERPRISE', label: 'Gupshup Enterprise' },
		{ value: 'THREE_SIXTY_DIALOG', label: '360Dialog' },
		{ value: 'WHATSAPP_CLOUD', label: 'WhatsApp Cloud' },
		{ value: 'AIRTEL', label: 'Airtel' },
		{ value: 'THREE_SIXTY_DIALOG_CLOUD', label: '360Dialog Cloud' },
		{ value: 'PINNACLE', label: 'Pinnacle' },
		{ value: 'PINNACLE_V3', label: 'Pinnacle V3' },
	]

	/**
	 * Currency options
	 */
	const currencyOptions: Array<{
		value: CurrencyType
		label: string
		symbol: string
	}> = [
		{ value: 'USD', label: 'US Dollar', symbol: '$' },
		{ value: 'INR', label: 'Indian Rupee', symbol: '₹' },
		{ value: 'EUR', label: 'Euro', symbol: '€' },
		{ value: 'IDR', label: 'Indonesian Rupiah', symbol: 'Rp' },
		{ value: 'AUD', label: 'Australian Dollar', symbol: 'A$' },
		{ value: 'GBP', label: 'British Pound', symbol: '£' },
	]

	/**
	 * Channel options
	 */
	const channelOptions: Array<{ value: ChannelType; label: string }> = [
		{ value: 'WHATSAPP', label: 'WhatsApp' },
		{ value: 'BIZ_CONVERSATION', label: 'Business Conversation' },
	]

	/**
	 * Fetch countries for filter dropdown
	 */
	const fetchCountries = async () => {
		setLoadingCountries(true)
		try {
			const countriesData = await pricingService.getCountries()
			setCountries(countriesData)
		} catch (error) {
			// Fallback to common countries if API fails
			setCountries([
				{ code: 'IN', name: 'India', phone_code: '91' },
				{ code: 'US', name: 'United States', phone_code: '1' },
				{ code: 'GB', name: 'United Kingdom', phone_code: '44' },
				{ code: 'AU', name: 'Australia', phone_code: '61' },
				{ code: 'ID', name: 'Indonesia', phone_code: '62' },
				{ code: 'DE', name: 'Germany', phone_code: '49' },
				{ code: 'FR', name: 'France', phone_code: '33' },
				{ code: 'CA', name: 'Canada', phone_code: '1' },
				{ code: 'BR', name: 'Brazil', phone_code: '55' },
				{ code: 'MX', name: 'Mexico', phone_code: '52' },
			])
		} finally {
			setLoadingCountries(false)
		}
	}

	/**
	 * Handle filter change
	 */
	const handleFilterChange = (key: keyof PricingFiltersType, value: any) => {
		const newFilters = { ...localFilters, [key]: value }
		setLocalFilters(newFilters)
		onFiltersChange(newFilters)
	}

	/**
	 * Clear all filters
	 */
	const handleClearFilters = () => {
		const emptyFilters = {}
		setLocalFilters(emptyFilters)
		onFiltersChange(emptyFilters)
	}

	/**
	 * Check if any filters are active
	 */
	const hasActiveFilters = Object.values(localFilters).some(
		value => value !== undefined && value !== ''
	)

	/**
	 * Fetch countries on component mount
	 */
	useEffect(() => {
		fetchCountries()
	}, [])

	return (
		<Card title='Filters' size='small'>
			<Row gutter={[16, 16]}>
				{/* Vendor Filter */}
				<Col xs={24} sm={12} md={6}>
					<Typography.Text strong>Vendor</Typography.Text>
					<Select
						placeholder='Select Vendor'
						style={{ width: '100%', marginTop: 4 }}
						value={localFilters.vendor}
						onChange={value => handleFilterChange('vendor', value)}
						allowClear
					>
						{vendorOptions.map(option => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
				</Col>

				{/* Currency Filter */}
				<Col xs={24} sm={12} md={6}>
					<Typography.Text strong>Currency</Typography.Text>
					<Select
						placeholder='Select Currency'
						style={{ width: '100%', marginTop: 4 }}
						value={localFilters.base_currency}
						onChange={value => handleFilterChange('base_currency', value)}
						allowClear
					>
						{currencyOptions.map(option => (
							<Option key={option.value} value={option.value}>
								{option.symbol} {option.label}
							</Option>
						))}
					</Select>
				</Col>

				{/* Country Filter */}
				<Col xs={24} sm={12} md={6}>
					<Typography.Text strong>Country</Typography.Text>
					<Select
						placeholder='Select Country'
						style={{ width: '100%', marginTop: 4 }}
						value={localFilters.country_code}
						onChange={value => handleFilterChange('country_code', value)}
						loading={loadingCountries}
						showSearch
						optionFilterProp='children'
						filterOption={(input, option) =>
							(option?.children as unknown as string)
								?.toLowerCase()
								.includes(input.toLowerCase())
						}
						allowClear
					>
						{countries.map(country => (
							<Option key={country.code} value={country.code}>
								{country.name} ({country.code})
							</Option>
						))}
					</Select>
				</Col>

				{/* Channel Filter */}
				<Col xs={24} sm={12} md={6}>
					<Typography.Text strong>Channel</Typography.Text>
					<Select
						placeholder='Select Channel'
						style={{ width: '100%', marginTop: 4 }}
						value={localFilters.channel}
						onChange={value => handleFilterChange('channel', value)}
						allowClear
					>
						{channelOptions.map(option => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
				</Col>

				{/* Action Buttons */}
				<Col xs={24} sm={24} md={24}>
					<Space>
						<Button type='primary' icon={<SearchOutlined />} onClick={onRefresh}>
							Search
						</Button>
						<Button icon={<ReloadOutlined />} onClick={onRefresh}>
							Refresh
						</Button>
						{hasActiveFilters && (
							<Button icon={<ClearOutlined />} onClick={handleClearFilters}>
								Clear Filters
							</Button>
						)}
					</Space>
				</Col>
			</Row>

			{/* Active Filters Display */}
			{hasActiveFilters && (
				<Row style={{ marginTop: 16 }}>
					<Col span={24}>
						<Typography.Text type='secondary'>
							Active filters:{' '}
							{Object.entries(localFilters)
								.filter(([, value]) => value !== undefined && value !== '')
								.map(([key, value]) => `${key}: ${value}`)
								.join(', ')}
						</Typography.Text>
					</Col>
				</Row>
			)}
		</Card>
	)
}

export default PricingFilters
